import React, { useRef, useState } from 'react';
import {
  FaCloudUploadAlt,
  FaEye,
  FaSearch,
} from 'react-icons/fa';
import UploadModal from './UploadModal';
import { styles } from './styles';
import { handleFileUpload, handleUploadModeExtraction } from './utils/uploadContentUtils';

const UploadFlow = ({
  selectedFileType,
  setSelectedFileType,
  uploadedFiles,
  setUploadedFiles,
  companyName,
  setCompanyName,
  tickerId,
  setTickerId,
  showFilesModal,
  setShowFilesModal,
  setIsProcessing,
  isProcessing,
  financialStatementsData,
  onExtractionComplete,
  removeFile
}) => {
  const fileInputRef = useRef(null);
  const [companySuggestions, setCompanySuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(false);

  // Fetch company suggestions
  const fetchCompanySuggestions = async (searchTerm) => {
    if (searchTerm.length < 2) {
      setCompanySuggestions([]);
      return;
    }
    
    setIsLoadingCompanies(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(
        `https://nirafinhub.niraitsolutions.com/api/v1/companies/suggestions?search=${encodeURIComponent(searchTerm)}&limit=10`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setCompanySuggestions(data.companies || []);
      }
    } catch (error) {
      console.error('Error fetching company suggestions:', error);
    } finally {
      setIsLoadingCompanies(false);
    }
  };

  // Handle company input change
  const handleCompanyChange = (e) => {
    const value = e.target.value;
    setCompanyName(value);
    
    if (value.length >= 2) {
      fetchCompanySuggestions(value);
      setShowSuggestions(true);
    } else {
      setCompanySuggestions([]);
      setShowSuggestions(false);
    }
  };

  // Select company from suggestions
  const handleSelectCompany = (company) => {
    setCompanyName(company.name);
    setTickerId(company.ticker_id || '');
    setCompanySuggestions([]);
    setShowSuggestions(false);
  };

  // Handle file upload
  const handleFileUploadWrapper = (files) => {
    const newFiles = handleFileUpload(files, uploadedFiles, false);
    if (newFiles.length > 0) {
      setUploadedFiles(prev => [...prev, ...newFiles]);
    }
  };

  // Handle file drop
  const handleFileDrop = (e) => {
    e.preventDefault();
    const files = e.dataTransfer.files;
    handleFileUploadWrapper(files);
  };

  // Handle form submission - UPDATED
  const handleUploadSubmit = async (e) => {
    e.preventDefault();
    
    // Show loading message
    setIsProcessing(true);
    
    // Create a callback for cleanup after successful upload
    const extractionCompleteCallback = (result) => {
      if (result && result.success) {
        // Clear form for next upload
        setUploadedFiles([]);
        setCompanyName('');
        setTickerId('');
        setSelectedFileType('Annual Files');
      }
    };
    
    try {
      await handleUploadModeExtraction(
        companyName,
        tickerId,
        uploadedFiles,
        selectedFileType,
        financialStatementsData,
        setIsProcessing,
        extractionCompleteCallback
      );
    } catch (error) {
      console.error('Upload error:', error);
      setIsProcessing(false);
    }
  };

  return (
    <div className="max-w-2xl">
      {/* Company Search with Suggestions */}
      <div className="mb-6 relative">
        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
          Company Name *
        </label>
        <div className="relative">
          <div className="relative">
            <input
              value={companyName}
              onChange={handleCompanyChange}
              onFocus={() => {
                if (companyName.length >= 2) {
                  setShowSuggestions(true);
                }
              }}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
              className="w-full px-3 py-2.5 pl-10 text-sm bg-white dark:bg-gray-800 border 
                         border-gray-300 dark:border-gray-700 rounded-md 
                         text-gray-900 dark:text-white 
                         focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 
                         transition-colors"
              placeholder="Search company by name..."
              required
            />
            <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
              <FaSearch className="w-4 h-4" />
            </div>
          </div>
          
          {/* Suggestions Dropdown */}
          {showSuggestions && companySuggestions.length > 0 && (
            <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border 
                          border-gray-300 dark:border-gray-700 rounded-md shadow-lg max-h-60 overflow-y-auto">
              {companySuggestions.map((company, index) => (
                <div
                  key={company.id}
                  onClick={() => handleSelectCompany(company)}
                  className={`px-3 py-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 
                            ${index === 0 ? 'rounded-t-md' : ''} 
                            ${index === companySuggestions.length - 1 ? 'rounded-b-md' : ''}`}
                >
                  <div className="font-medium text-gray-900 dark:text-white">
                    {company.name}
                  </div>
                  {company.ticker_id && (
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      Ticker: {company.ticker_id}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          
          {isLoadingCompanies && (
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
            </div>
          )}
        </div>
        
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          Start typing to search for companies in the database
        </p>
      </div>

      {/* File Type Selection */}
      <div className="mb-6">
        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
          File Type *
        </label>
        <div className="flex space-x-2">
          <button
            type="button"
            onClick={() => setSelectedFileType('Annual Files')}
            className={styles.fileTypeButton(selectedFileType === 'Annual Files')}
          >
            Annual Files
          </button>
          <button
            type="button"
            onClick={() => setSelectedFileType('Quarterly Files')}
            className={styles.fileTypeButton(selectedFileType === 'Quarterly Files')}
          >
            Quarterly Files
          </button>
        </div>
      </div>

      {/* Upload Box Section */}
      <div className="mb-6">
        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
          Upload PDF Files *
        </label>
        
        <div className="flex justify-center">
          <div className="w-full max-w-md">
            <div
              onDrop={handleFileDrop}
              onDragOver={(e) => e.preventDefault()}
              onClick={() => fileInputRef.current.click()}
              className={styles.uploadBox(uploadedFiles.length > 0)}
            >
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                accept=".pdf"
                multiple
                onChange={(e) => handleFileUploadWrapper(e.target.files)}
              />

              <FaCloudUploadAlt className="text-3xl text-blue-500 dark:text-blue-400 mb-3 mx-auto" />

              <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                Click to upload or drag and drop
              </p>

              <p className="text-xs text-gray-500 dark:text-gray-400">
                PDF files only • Maximum 15 files
              </p>
              
              {uploadedFiles.length > 0 && (
                <p className="text-xs text-green-600 dark:text-green-400 mt-2 font-medium">
                  {uploadedFiles.length} file{uploadedFiles.length !== 1 ? 's' : ''} uploaded
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Uploaded Files Button */}
        {uploadedFiles.length > 0 && (
          <div className="mt-4 flex justify-center">
            <button
              type="button"
              onClick={() => setShowFilesModal(true)}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 dark:text-blue-400 
                         bg-blue-50 dark:bg-blue-900/20 rounded-md hover:bg-blue-100 dark:hover:bg-blue-900/30 
                         transition-colors border border-blue-200 dark:border-blue-800"
            >
              <FaEye className="text-sm" />
              View Uploaded Files ({uploadedFiles.length})
            </button>
          </div>
        )}
      </div>

      {/* Ticker ID (Optional) */}
      <div className="mb-6">
        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
          Ticker ID (Optional)
        </label>
        <input
          value={tickerId}
          onChange={(e) => setTickerId(e.target.value)}
          className="w-full px-3 py-2.5 text-sm bg-white dark:bg-gray-800 border 
                     border-gray-300 dark:border-gray-700 rounded-md 
                     text-gray-900 dark:text-white 
                     focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 
                     transition-colors"
          placeholder="Enter ticker (optional)"
        />
      </div>

      {/* Submit Button */}
      <div className="flex justify-center pt-2">
        <button
          type="submit"
          onClick={handleUploadSubmit}
          disabled={uploadedFiles.length === 0 || !companyName || isProcessing}
          className={styles.submitButton(uploadedFiles.length === 0 || !companyName || isProcessing)}
        >
          {isProcessing ? (
            <span className="flex items-center justify-center gap-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              Processing and Extracting Data...
            </span>
          ) : (
            'Extract Data'
          )}
        </button>
      </div>

      {/* Upload Modal */}
      <UploadModal
        showFilesModal={showFilesModal}
        setShowFilesModal={setShowFilesModal}
        uploadedFiles={uploadedFiles}
        removeFile={removeFile}
      />
    </div>
  );
};

export default UploadFlow;